import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { ProductSearchFacet, ProductSearchFacetCode } from '@/models/Sku';

interface ProductCatalogFacetSelection {
  supplierIds: string[];
  productCategoryIds: string[];
  promotions: boolean;
}

interface ProductCatalogFacetsProps {
  facets: ProductSearchFacet[];
  selection: ProductCatalogFacetSelection;
  onToggleValue: (facetCode: ProductSearchFacetCode, value: string, checked: boolean) => void;
  onClear: () => void;
}

const facetOrder: ProductSearchFacetCode[] = ['supplier', 'category', 'promotion'];

const getSelectedValues = (
  facetCode: ProductSearchFacetCode,
  selection: ProductCatalogFacetSelection,
): string[] => {
  switch (facetCode) {
    case 'supplier':
      return selection.supplierIds;
    case 'category':
      return selection.productCategoryIds;
    case 'promotion':
      return selection.promotions ? ['true'] : [];
  }
};

const getFacetValueCount = (value: ProductSearchFacet['values'][number]): number | undefined =>
  value.count ?? value.estimatedCount;

const ProductCatalogFacets = ({
  facets,
  selection,
  onToggleValue,
  onClear,
}: ProductCatalogFacetsProps) => {
  const orderedFacets = facetOrder
    .map((code) => facets.find((facet) => facet.code === code))
    .filter((facet): facet is ProductSearchFacet => facet !== undefined);

  const hasSelection =
    selection.supplierIds.length > 0 ||
    selection.productCategoryIds.length > 0 ||
    selection.promotions;

  return (
    <aside
      className="border border-gray-200 rounded-md bg-white p-4"
      data-testid="product-catalog-facets"
    >
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
        {hasSelection && (
          <Button variant="plain" size="xs" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>

      <div className="space-y-6">
        {orderedFacets.map((facet) => {
          const selectedValues = getSelectedValues(facet.code, selection);

          return (
            <section key={facet.code}>
              <h3 className="text-xs font-semibold uppercase text-gray-500 mb-3">{facet.name}</h3>
              <div className="space-y-2">
                {facet.values.map((value) => {
                  const checkboxId = `facet-${facet.code}-${value.value}`;
                  const checked = selectedValues.includes(value.value);
                  const count = getFacetValueCount(value);

                  return (
                    <label
                      key={value.value}
                      htmlFor={checkboxId}
                      className="flex items-center justify-between gap-3 text-sm text-gray-700"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <Checkbox
                          id={checkboxId}
                          checked={checked}
                          onChange={(isChecked) =>
                            onToggleValue(facet.code, value.value, isChecked)
                          }
                        />
                        <span className="truncate">{value.label}</span>
                      </span>
                      {count !== undefined && (
                        <span className="text-xs tabular-nums text-gray-500">{count}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </aside>
  );
};

export default ProductCatalogFacets;
